import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Loader2, DollarSign, TrendingUp, ExternalLink, Calendar, Users } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';

interface CompanyFundingTabProps {
  companyId: string;
  companyName: string;
}

interface FundingRound {
  id: string;
  round_type: string;
  amount: number | null;
  currency: string;
  announced_date: string | null;
  lead_investors: string[] | null;
  all_investors: string[] | null;
  valuation: number | null;
  source_url: string | null;
  is_ai_discovered: boolean;
}

const roundTypeConfig: Record<string, { color: string; label: string }> = {
  seed: { color: 'bg-green-500/10 text-green-600', label: 'Seed' },
  pre_seed: { color: 'bg-lime-500/10 text-lime-600', label: 'Pre-Seed' },
  series_a: { color: 'bg-blue-500/10 text-blue-600', label: 'Series A' },
  series_b: { color: 'bg-indigo-500/10 text-indigo-600', label: 'Series B' },
  series_c: { color: 'bg-purple-500/10 text-purple-600', label: 'Series C' },
  series_d: { color: 'bg-violet-500/10 text-violet-600', label: 'Series D' },
  series_e: { color: 'bg-fuchsia-500/10 text-fuchsia-600', label: 'Series E' },
  ipo: { color: 'bg-amber-500/10 text-amber-600', label: 'IPO' },
  acquisition: { color: 'bg-red-500/10 text-red-600', label: 'Acquisition' },
  debt: { color: 'bg-gray-500/10 text-gray-600', label: 'Debt' },
  grant: { color: 'bg-cyan-500/10 text-cyan-600', label: 'Grant' },
};

const formatAmount = (amount: number | null, currency: string) => {
  if (!amount) return 'Undisclosed';
  if (amount >= 1_000_000_000) {
    return `${currency === 'USD' ? '$' : currency}${(amount / 1_000_000_000).toFixed(1)}B`;
  }
  if (amount >= 1_000_000) {
    return `${currency === 'USD' ? '$' : currency}${(amount / 1_000_000).toFixed(1)}M`;
  }
  return `${currency === 'USD' ? '$' : currency}${(amount / 1000).toFixed(0)}K`;
};

export function CompanyFundingTab({ companyId, companyName }: CompanyFundingTabProps) {
  const [fundingRounds, setFundingRounds] = useState<FundingRound[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [totalFunding, setTotalFunding] = useState(0);

  const fetchFunding = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('company_funding_rounds')
        .select('*')
        .eq('company_id', companyId)
        .order('announced_date', { ascending: false });

      if (error) throw error;
      setFundingRounds(data || []);
      
      // Calculate total funding
      const total = (data || []).reduce((sum, round) => sum + (round.amount || 0), 0);
      setTotalFunding(total);
    } catch (error) {
      console.error('Error fetching funding:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchFunding();
  }, [companyId]);

  const getRoundConfig = (type: string) => {
    return roundTypeConfig[type] || { color: 'bg-gray-500/10 text-gray-600', label: type };
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Funding History</h3>
          {totalFunding > 0 && (
            <p className="text-sm text-muted-foreground">
              Total raised: <span className="font-semibold text-green-600">{formatAmount(totalFunding, 'USD')}</span>
            </p>
          )}
        </div>
      </div>

      {fundingRounds.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <DollarSign className="h-12 w-12 text-muted-foreground/50 mb-4" />
            <h4 className="font-medium text-muted-foreground mb-2">No funding data yet</h4>
            <p className="text-sm text-muted-foreground mb-4">
              Use "AI Enrich" to discover funding history for {companyName}
            </p>
          </CardContent>
        </Card>
      ) : (
        <ScrollArea className="h-[500px]">
          <div className="space-y-3 pr-4">
            {fundingRounds.map((round) => {
              const config = getRoundConfig(round.round_type);
              
              return (
                <Card key={round.id}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge className={config.color}>{config.label}</Badge>
                          <span className="text-xl font-bold">{formatAmount(round.amount, round.currency)}</span>
                          {round.valuation && (
                            <Badge variant="outline" className="text-xs">
                              <TrendingUp className="h-3 w-3 mr-1" />
                              {formatAmount(round.valuation, round.currency)} valuation
                            </Badge>
                          )}
                        </div>
                        
                        {round.lead_investors && round.lead_investors.length > 0 && (
                          <div className="flex items-center gap-2 mt-2 text-sm">
                            <Users className="h-4 w-4 text-muted-foreground" />
                            <span className="text-muted-foreground">Lead:</span>
                            <span className="font-medium">{round.lead_investors.join(', ')}</span>
                          </div>
                        )}
                        
                        {round.all_investors && round.all_investors.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {round.all_investors.slice(0, 5).map((investor, i) => (
                              <Badge key={i} variant="secondary" className="text-xs">
                                {investor}
                              </Badge>
                            ))}
                            {round.all_investors.length > 5 && (
                              <Badge variant="secondary" className="text-xs">
                                +{round.all_investors.length - 5} more
                              </Badge>
                            )}
                          </div>
                        )}
                        
                        <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                          {round.announced_date && (
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {format(new Date(round.announced_date), 'MMM d, yyyy')}
                            </span>
                          )}
                          {round.source_url && (
                            <a
                              href={round.source_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-1 text-primary hover:underline"
                            >
                              <ExternalLink className="h-3 w-3" />
                              Source
                            </a>
                          )}
                          {round.is_ai_discovered && (
                            <Badge variant="outline" className="text-xs">AI Discovered</Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </ScrollArea>
      )}
    </div>
  );
}
